// A datatable's current page is persisted in the URL, alongside its filters,
// so the page survives navigation (open a row, come back) and the URL is
// shareable — a link opens on the same page the sender was viewing.
//
// The param is namespaced by table id (`<id>_page`) rather than a bare `page`
// because a single route can host several datatables (e.g. the invoices,
// quotes and sub-customers tables on a customer detail page); a shared `page`
// would have them fight over one value. Tables without an id opt out.

// The URL param name for a table's page, or null when the table has no id and
// therefore cannot participate safely.
export const pageParamName = (id?: string): string | null =>
  id ? `${id}_page` : null;

// The 1-based page for the given table read from a URL search string. Returns
// null when the param is absent, non-numeric, or less than 1 — all of which
// mean "no stored page", i.e. fall back to page 1.
export const readPageFromUrl = (
  id: string | undefined,
  search: string = window.location.search
): number | null => {
  const name = pageParamName(id);
  if (!name) return null;

  const raw = new URLSearchParams(search).get(name);
  if (raw === null) return null;

  const page = Number(raw);
  if (!Number.isInteger(page) || page < 1) return null;

  return page;
};

// The value to write for a page. Page 1 is the default, so it is written as
// null — which strips the param — to keep URLs clean.
export const pageParamValue = (page: number): string | null =>
  page > 1 ? String(page) : null;

// DataTables' displayStart is a zero-based record offset, not a page number.
export const pageDisplayStart = (page: number, pageLength: number): number =>
  Math.max(0, (page - 1) * pageLength);
